// Documents in a document knowledge base (P2.S6.T7, ADR-019).
//
// Built for a 50,000-document corpus rather than a demo one, which drives three
// choices that would look like over-engineering at a smaller size:
//
//  * Cursor pagination, not offset. The corpus is written to while it is read,
//    so offsets shift under concurrent ingestion and an operator paging through
//    would see rows twice or miss them.
//  * Status counts come from the server for the whole KB, not from the loaded
//    page. "3 failed" has to be visible without paging through 50,000 rows.
//  * Failure reasons are shown inline. Nobody is going log-diving to find out
//    why one file out of fifty thousand did not index.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, FileText, Loader2, RefreshCw, Upload } from "lucide-react";
import * as React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  selectClass,
} from "@/components/ui/primitives";
import { EmptyState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { ApiError } from "@/lib/api/client";
import {
  type KnowledgeDocumentView,
  createDocumentUploadUrl,
  listKbDocuments,
  uploadToPresignedUrl,
} from "@/lib/api/endpoints";
import { formatDate } from "@/lib/utils";

const PAGE_SIZE = 50;

/** Accepted client-side so an operator is refused instantly rather than after
 *  an upload the API would reject. Mirrors SUPPORTED_EXTENSIONS on the server. */
const ACCEPT = ".pdf,.docx,.md,.markdown,.txt,.json";

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "secondary"> = {
  ready: "success",
  ingesting: "warning",
  pending: "secondary",
  failed: "destructive",
  skipped: "secondary",
};

export function KbDocumentsPanel({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const [cursor, setCursor] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string>("");
  // A stack of the cursors we came from, so "Previous" works without
  // re-querying from the start.
  const [history, setHistory] = React.useState<(string | null)[]>([]);
  const fileInput = React.useRef<HTMLInputElement>(null);

  const page = useQuery({
    queryKey: ["kb", slug, "documents", cursor, statusFilter],
    queryFn: () =>
      listKbDocuments(slug, { cursor, status: statusFilter || null, limit: PAGE_SIZE }),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const contentType = file.type || "application/octet-stream";
      // Two steps on purpose: the API only signs, the browser transfers. The
      // bytes never touch Aviary (ADR-013, ADR-019).
      const signed = await createDocumentUploadUrl(slug, file.name, contentType);
      await uploadToPresignedUrl(signed.url, file, contentType);
      return signed.object_key;
    },
    onSuccess: async (key) => {
      toast.success(`Uploaded ${key}. Run Ingest to index it.`);
      await qc.invalidateQueries({ queryKey: ["kb", slug, "documents"] });
    },
    onError: (e: Error) =>
      toast.error(e instanceof ApiError ? e.message : "Upload failed. Please try again."),
  });

  const counts = page.data?.status_counts ?? {};
  const failed = counts.failed ?? 0;

  const goNext = () => {
    const next = page.data?.next_cursor ?? null;
    if (!next) return;
    setHistory((h) => [...h, cursor]);
    setCursor(next);
  };

  const goBack = () => {
    setHistory((h) => {
      const previous = h[h.length - 1] ?? null;
      setCursor(previous);
      return h.slice(0, -1);
    });
  };

  const changeFilter = (value: string) => {
    setStatusFilter(value);
    setCursor(null);
    setHistory([]);
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">
            Documents{page.data ? ` (${page.data.total.toLocaleString()})` : ""}
          </CardTitle>
          {failed > 0 ? (
            <Badge variant="destructive">{failed.toLocaleString()} failed</Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <select
            className={`${selectClass} h-9 w-40`}
            value={statusFilter}
            onChange={(e) => changeFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {["ready", "pending", "ingesting", "failed", "skipped"].map((s) => (
              <option key={s} value={s}>
                {s} {counts[s] ? `(${counts[s]})` : ""}
              </option>
            ))}
          </select>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              // Reset so selecting the same file twice fires again.
              e.target.value = "";
            }}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void page.refetch()}
            aria-label="Refresh documents"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {page.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : page.data && page.data.documents.length === 0 ? (
          <EmptyState
            title={statusFilter ? `No ${statusFilter} documents` : "No documents yet"}
            hint={
              statusFilter
                ? "Clear the filter to see the rest of the corpus."
                : "Upload a file, or add documents to the bucket prefix and run Ingest."
            }
          />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Document</TH>
                  <TH>Status</TH>
                  <TH>Chunks</TH>
                  <TH>Ingested</TH>
                </TR>
              </THead>
              <TBody>
                {(page.data?.documents ?? []).map((doc) => (
                  <DocumentRow key={doc.id} doc={doc} />
                ))}
              </TBody>
            </Table>

            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-muted-foreground">
                {page.data?.documents.length ?? 0} of {page.data?.total.toLocaleString() ?? 0}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goBack}
                  disabled={history.length === 0}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goNext}
                  disabled={!page.data?.next_cursor}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentRow({ doc }: { doc: KnowledgeDocumentView }) {
  return (
    <>
      <TR>
        <TD>
          <div className="font-medium">{doc.filename}</div>
          <div className="font-mono text-xs text-muted-foreground">{doc.object_key}</div>
        </TD>
        <TD>
          <Badge variant={STATUS_VARIANT[doc.status] ?? "secondary"}>{doc.status}</Badge>
        </TD>
        <TD className="tabular-nums">{doc.chunk_count || "—"}</TD>
        <TD className="text-xs text-muted-foreground">
          {doc.ingested_at ? formatDate(doc.ingested_at) : "—"}
        </TD>
      </TR>
      {doc.last_error ? (
        <TR>
          {/* The reason sits directly under its document rather than behind a
              click: at this corpus size, "which of my 50,000 files broke and
              why" must be answerable by scrolling. */}
          <TD colSpan={4} className="pt-0">
            <p className="flex items-start gap-1.5 rounded-md bg-destructive/5 px-3 py-2 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {doc.last_error}
            </p>
          </TD>
        </TR>
      ) : null}
    </>
  );
}
